import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get } from '../utils/api';
import {
  formatFinancialNumber,
  formatRatio,
  formatEarningsPerShare,
  formatPercentage,
  formatYield
} from '../utils/formatting';
import type { StockCategoryFilter } from '../types/stockCategories';

interface TwelveDataStatistics {
  meta: {
    symbol: string;
    name: string;
  };
  statistics: {
    valuations_metrics?: {
      market_capitalization?: number;
      enterprise_value?: number;
      trailing_pe?: number;
      forward_pe?: number;
      peg_ratio?: number;
      price_to_sales_ttm?: number;
      price_to_book_mrq?: number;
      enterprise_to_revenue?: number;
    };
    financials?: {
      operating_margin?: number;
      profit_margin?: number;
      gross_margin?: number;
      income_statement?: {
        revenue_ttm?: number;
        net_income_to_common_ttm?: number;
        diluted_eps_ttm?: number;
        quarterly_revenue_growth?: number;
        gross_profit_ttm?: number;
        revenue_per_share_ttm?: number;
        quarterly_earnings_growth_yoy?: number;
      };
      balance_sheet?: {
        total_cash_mrq?: number;
        total_debt_mrq?: number;
        total_debt_to_equity_mrq?: number;
        book_value_per_share_mrq?: number;
      };
      cash_flow?: {
        levered_free_cash_flow_ttm?: number;
        operating_cash_flow_ttm?: number;
      };
    };
    stock_statistics?: {
      shares_outstanding?: number;
      percent_held_by_institutions?: number;
      percent_held_by_insiders?: number;
      short_ratio?: number;
    };
    dividends_and_splits?: {
      forward_annual_dividend_yield?: number;
      '5_year_average_dividend_yield'?: number | null;
    };
    stock_price_summary?: {
      beta?: number;
    };
  };
}

interface FinancialMetricsData {
  marketCapitalization: string | null;
  annualRevenue: string | null;
  priceToSales: string | null;
  earnings: string | null;
  earningsPerShare: string | null;
  priceToEarnings: string | null;
  totalCash: string | null;
  totalDebt: string | null;
  enterpriseValue: string | null;
  bookValue: string | null;
  dividendYield: string | null;
  beta: string | null;
  forwardPE: string | null;
  pegRatio: string | null;
  debtToEquity: string | null;
  quarterlyRevenueGrowth: string | null;
  leveredFreeCashFlow: string | null;
  fiveYearDividendYield: string | null;
}

interface FormattedFinancialMetrics {
  // Group 1: Cash & Debt
  cash: string | null;
  debt: string | null;
  // Group 2: Company Size
  marketCap: string | null;
  enterpriseValue: string | null;
  debtToEquity: string | null;
  // Group 3: Profitability & Growth
  annualRevenue: string | null;
  quarterlyRevenueGrowth: string | null;
  leveredFreeCashFlow: string | null;
  netIncome: string | null;
  dilutedEps: string | null;
  // Group 4: Dividends
  fiveYearDividendYield: string | null;
  // Group 5: Valuation Ratios
  priceToSales: string | null;
  industryPriceToSales: string | null;
  trailingPE: string | null;
  industryTrailingPE: string | null;
  // Legacy fields (for backwards compatibility)
  earnings: string | null;
  earningsPerShare: string | null;
  priceToEarnings: string | null;
  // New dynamic group metrics
  operatingMargin: string | null;
  profitMargin: string | null;
  grossMargin: string | null;
  operatingCashFlow: string | null;
  revenuePerShare: string | null;
  bookValuePerShare: string | null;
  forwardPE: string | null;
  priceToBook: string | null;
  evToRevenue: string | null;
  pegRatio: string | null;
  earningsGrowthYoY: string | null;
  institutionalOwnership: string | null;
  insiderOwnership: string | null;
  shortRatio: string | null;
}

interface FinancialMetricsAvailability {
  // Group 1: Cash & Debt
  hasCash: boolean;
  hasDebt: boolean;
  // Group 2: Company Size
  hasMarketCap: boolean;
  hasEnterpriseValue: boolean;
  hasDebtToEquity: boolean;
  // Group 3: Profitability & Growth
  hasRevenue: boolean;
  hasQuarterlyRevenueGrowth: boolean;
  hasLeveredFreeCashFlow: boolean;
  hasNetIncome: boolean;
  hasDilutedEps: boolean;
  // Group 4: Dividends
  hasFiveYearDividendYield: boolean;
  // Group 5: Valuation Ratios
  hasPriceToSales: boolean;
  hasPE: boolean;
  // Legacy
  hasEPS: boolean;
}

interface FinancialMetricsResult {
  formatted: FormattedFinancialMetrics;
  raw: FinancialMetricsData | null;
  symbol: string | null;
  companyName: string | null;
  availability: FinancialMetricsAvailability | null;
  selectedGroups: [string, string] | null;
}

interface UseFinancialMetricsReturn {
  metrics: FormattedFinancialMetrics;
  rawMetrics: FinancialMetricsData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  category: StockCategoryFilter;
  symbol: string | null;
  companyName: string | null;
  dataAvailability: FinancialMetricsAvailability | null;
  selectedGroups: [string, string] | null;
  hasData: boolean;
}

interface UseFinancialMetricsOptions {
  category: StockCategoryFilter;
  autoFetch?: boolean;
  date?: string;
}

const DEFAULT_FORMATTED: FormattedFinancialMetrics = {
  // Group 1: Cash & Debt
  cash: null,
  debt: null,
  // Group 2: Company Size
  marketCap: null,
  enterpriseValue: null,
  debtToEquity: null,
  // Group 3: Profitability & Growth
  annualRevenue: null,
  quarterlyRevenueGrowth: null,
  leveredFreeCashFlow: null,
  netIncome: null,
  dilutedEps: null,
  // Group 4: Dividends
  fiveYearDividendYield: null,
  // Group 5: Valuation Ratios
  priceToSales: null,
  industryPriceToSales: null,
  trailingPE: null,
  industryTrailingPE: null,
  // Legacy
  earnings: null,
  earningsPerShare: null,
  priceToEarnings: null,
  // New dynamic group metrics
  operatingMargin: null,
  profitMargin: null,
  grossMargin: null,
  operatingCashFlow: null,
  revenuePerShare: null,
  bookValuePerShare: null,
  forwardPE: null,
  priceToBook: null,
  evToRevenue: null,
  pegRatio: null,
  earningsGrowthYoY: null,
  institutionalOwnership: null,
  insiderOwnership: null,
  shortRatio: null,
};

const extractMetricsData = (stats: TwelveDataStatistics['statistics']): FinancialMetricsData => ({
  marketCapitalization: stats.valuations_metrics?.market_capitalization?.toString() ?? null,
  annualRevenue: stats.financials?.income_statement?.revenue_ttm?.toString() ?? null,
  priceToSales: stats.valuations_metrics?.price_to_sales_ttm?.toString() ?? null,
  earnings: stats.financials?.income_statement?.net_income_to_common_ttm?.toString() ?? null,
  earningsPerShare: stats.financials?.income_statement?.diluted_eps_ttm?.toString() ?? null,
  priceToEarnings: stats.valuations_metrics?.trailing_pe?.toString() ?? null,
  totalCash: stats.financials?.balance_sheet?.total_cash_mrq?.toString() ?? null,
  totalDebt: stats.financials?.balance_sheet?.total_debt_mrq?.toString() ?? null,
  enterpriseValue: stats.valuations_metrics?.enterprise_value?.toString() ?? null,
  bookValue: stats.financials?.balance_sheet?.book_value_per_share_mrq?.toString() ?? null,
  dividendYield: stats.dividends_and_splits?.forward_annual_dividend_yield?.toString() ?? null,
  beta: stats.stock_price_summary?.beta?.toString() ?? null,
  forwardPE: stats.valuations_metrics?.forward_pe?.toString() ?? null,
  pegRatio: stats.valuations_metrics?.peg_ratio?.toString() ?? null,
  debtToEquity: stats.financials?.balance_sheet?.total_debt_to_equity_mrq?.toString() ?? null,
  quarterlyRevenueGrowth: stats.financials?.income_statement?.quarterly_revenue_growth?.toString() ?? null,
  leveredFreeCashFlow: stats.financials?.cash_flow?.levered_free_cash_flow_ttm?.toString() ?? null,
  fiveYearDividendYield: stats.dividends_and_splits?.['5_year_average_dividend_yield']?.toString() ?? null
});

const formatMetrics = (stats: TwelveDataStatistics['statistics']): FormattedFinancialMetrics => ({
  // Group 1: Cash & Debt
  cash: stats.financials?.balance_sheet?.total_cash_mrq != null
    ? formatFinancialNumber(stats.financials.balance_sheet.total_cash_mrq)
    : null,
  debt: stats.financials?.balance_sheet?.total_debt_mrq != null
    ? formatFinancialNumber(stats.financials.balance_sheet.total_debt_mrq)
    : null,
  // Group 2: Company Size
  marketCap: stats.valuations_metrics?.market_capitalization != null
    ? formatFinancialNumber(stats.valuations_metrics.market_capitalization)
    : null,
  enterpriseValue: stats.valuations_metrics?.enterprise_value != null
    ? formatFinancialNumber(stats.valuations_metrics.enterprise_value)
    : null,
  debtToEquity: stats.financials?.balance_sheet?.total_debt_to_equity_mrq != null
    ? formatRatio(stats.financials.balance_sheet.total_debt_to_equity_mrq / 100)
    : null,
  // Group 3: Profitability & Growth
  annualRevenue: stats.financials?.income_statement?.revenue_ttm != null
    ? formatFinancialNumber(stats.financials.income_statement.revenue_ttm)
    : null,
  quarterlyRevenueGrowth: stats.financials?.income_statement?.quarterly_revenue_growth != null
    ? formatPercentage(stats.financials.income_statement.quarterly_revenue_growth * 100, 1, true)
    : null,
  leveredFreeCashFlow: stats.financials?.cash_flow?.levered_free_cash_flow_ttm != null
    ? formatFinancialNumber(stats.financials.cash_flow.levered_free_cash_flow_ttm)
    : null,
  netIncome: stats.financials?.income_statement?.net_income_to_common_ttm != null
    ? formatFinancialNumber(stats.financials.income_statement.net_income_to_common_ttm)
    : null,
  dilutedEps: stats.financials?.income_statement?.diluted_eps_ttm != null
    ? formatEarningsPerShare(stats.financials.income_statement.diluted_eps_ttm)
    : null,
  // Group 4: Dividends
  fiveYearDividendYield: stats.dividends_and_splits?.['5_year_average_dividend_yield'] != null
    ? formatYield(stats.dividends_and_splits['5_year_average_dividend_yield'])
    : null,
  // Group 5: Valuation Ratios
  priceToSales: stats.valuations_metrics?.price_to_sales_ttm != null
    ? formatRatio(stats.valuations_metrics.price_to_sales_ttm)
    : null,
  industryPriceToSales: null, // Industry ratios come from pre-computed formatted data
  trailingPE: stats.valuations_metrics?.trailing_pe != null
    ? formatRatio(stats.valuations_metrics.trailing_pe)
    : null,
  industryTrailingPE: null, // Industry ratios come from pre-computed formatted data
  // Legacy fields (for backwards compatibility)
  earnings: stats.financials?.income_statement?.net_income_to_common_ttm != null
    ? formatFinancialNumber(stats.financials.income_statement.net_income_to_common_ttm)
    : null,
  earningsPerShare: stats.financials?.income_statement?.diluted_eps_ttm != null
    ? formatEarningsPerShare(stats.financials.income_statement.diluted_eps_ttm)
    : null,
  priceToEarnings: stats.valuations_metrics?.trailing_pe != null
    ? formatRatio(stats.valuations_metrics.trailing_pe)
    : null,
  // New dynamic group metrics
  operatingMargin: stats.financials?.operating_margin != null
    ? formatPercentage(stats.financials.operating_margin * 100)
    : null,
  profitMargin: stats.financials?.profit_margin != null
    ? formatPercentage(stats.financials.profit_margin * 100)
    : null,
  grossMargin: stats.financials?.gross_margin != null
    ? formatPercentage(stats.financials.gross_margin * 100)
    : null,
  operatingCashFlow: stats.financials?.cash_flow?.operating_cash_flow_ttm != null
    ? formatFinancialNumber(stats.financials.cash_flow.operating_cash_flow_ttm)
    : null,
  revenuePerShare: stats.financials?.income_statement?.revenue_per_share_ttm != null
    ? formatEarningsPerShare(stats.financials.income_statement.revenue_per_share_ttm)
    : null,
  bookValuePerShare: stats.financials?.balance_sheet?.book_value_per_share_mrq != null
    ? formatEarningsPerShare(stats.financials.balance_sheet.book_value_per_share_mrq)
    : null,
  forwardPE: stats.valuations_metrics?.forward_pe != null
    ? formatRatio(stats.valuations_metrics.forward_pe)
    : null,
  priceToBook: stats.valuations_metrics?.price_to_book_mrq != null
    ? formatRatio(stats.valuations_metrics.price_to_book_mrq)
    : null,
  evToRevenue: stats.valuations_metrics?.enterprise_to_revenue != null
    ? formatRatio(stats.valuations_metrics.enterprise_to_revenue)
    : null,
  pegRatio: stats.valuations_metrics?.peg_ratio != null
    ? formatRatio(stats.valuations_metrics.peg_ratio)
    : null,
  earningsGrowthYoY: stats.financials?.income_statement?.quarterly_earnings_growth_yoy != null
    ? formatPercentage(stats.financials.income_statement.quarterly_earnings_growth_yoy * 100, 1, true)
    : null,
  institutionalOwnership: stats.stock_statistics?.percent_held_by_institutions != null
    ? formatPercentage(stats.stock_statistics.percent_held_by_institutions * 100)
    : null,
  insiderOwnership: stats.stock_statistics?.percent_held_by_insiders != null
    ? formatPercentage(stats.stock_statistics.percent_held_by_insiders * 100)
    : null,
  shortRatio: stats.stock_statistics?.short_ratio != null
    ? formatRatio(stats.stock_statistics.short_ratio)
    : null,
});

const deriveAvailability = (stats: TwelveDataStatistics['statistics']): FinancialMetricsAvailability => ({
  // Group 1: Cash & Debt
  hasCash: !!stats.financials?.balance_sheet?.total_cash_mrq,
  hasDebt: !!stats.financials?.balance_sheet?.total_debt_mrq,
  // Group 2: Company Size
  hasMarketCap: !!stats.valuations_metrics?.market_capitalization,
  hasEnterpriseValue: !!stats.valuations_metrics?.enterprise_value,
  hasDebtToEquity: !!stats.financials?.balance_sheet?.total_debt_to_equity_mrq,
  // Group 3: Profitability & Growth
  hasRevenue: !!stats.financials?.income_statement?.revenue_ttm,
  hasQuarterlyRevenueGrowth: !!stats.financials?.income_statement?.quarterly_revenue_growth,
  hasLeveredFreeCashFlow: !!stats.financials?.cash_flow?.levered_free_cash_flow_ttm,
  hasNetIncome: !!stats.financials?.income_statement?.net_income_to_common_ttm,
  hasDilutedEps: !!stats.financials?.income_statement?.diluted_eps_ttm,
  // Group 4: Dividends
  hasFiveYearDividendYield: !!stats.dividends_and_splits?.['5_year_average_dividend_yield'],
  // Group 5: Valuation Ratios
  hasPriceToSales: !!stats.valuations_metrics?.price_to_sales_ttm,
  hasPE: !!stats.valuations_metrics?.trailing_pe,
  // Legacy
  hasEPS: !!stats.financials?.income_statement?.diluted_eps_ttm
});

const resolveMetricsPayload = (payload: unknown): any => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const candidate = payload as Record<string, any>;

  if (candidate.processed_financial_metrics && typeof candidate.processed_financial_metrics === 'object') {
    return candidate.processed_financial_metrics;
  }

  if (candidate.statistics && typeof candidate.statistics === 'object') {
    return candidate;
  }

  if (candidate.formatted && typeof candidate.formatted === 'object') {
    return candidate;
  }

  if (candidate.data && typeof candidate.data === 'object') {
    const nested = resolveMetricsPayload(candidate.data);
    if (nested) {
      return nested;
    }
  }

  if (candidate.stock && typeof candidate.stock === 'object') {
    const nested = resolveMetricsPayload(candidate.stock);
    if (nested) {
      return nested;
    }
  }

  return null;
};

const normaliseResponse = (response: any): FinancialMetricsResult => {
  const payload =
    response && typeof response === 'object' && 'data' in response
      ? (response as { data: unknown }).data
      : response;

  const metricsPayload = resolveMetricsPayload(payload);

  if (!metricsPayload || typeof metricsPayload !== 'object') {
    throw new Error('Invalid financial metrics response');
  }

  const statistics = (metricsPayload as { statistics?: TwelveDataStatistics['statistics'] }).statistics;
  const preFormatted = (metricsPayload as { formatted?: FormattedFinancialMetrics | null }).formatted;

  if (!statistics && !preFormatted) {
    throw new Error('Missing financial metrics statistics');
  }

  const formatted = preFormatted ?? formatMetrics(statistics!);

  // Map earningsPerShare to dilutedEps if dilutedEps is missing (they're the same metric)
  if (formatted && !formatted.dilutedEps && formatted.earningsPerShare) {
    formatted.dilutedEps = formatted.earningsPerShare;
  }

  const raw =
    (metricsPayload as { raw?: FinancialMetricsData | null }).raw ?? (statistics ? extractMetricsData(statistics) : null);
  const availability =
    (metricsPayload as { availability?: FinancialMetricsAvailability | null }).availability ??
    (statistics ? deriveAvailability(statistics) : null);

  const payloadObject = payload && typeof payload === 'object'
    ? (payload as Record<string, any>)
    : {};

  const symbol =
    payloadObject.symbol ??
    payloadObject.stock?.Symbol ??
    payloadObject.meta?.symbol ??
    (metricsPayload as { meta?: { symbol?: string } }).meta?.symbol ??
    null;

  const companyName =
    payloadObject.companyName ??
    payloadObject.stock?.Security ??
    payloadObject.meta?.name ??
    (metricsPayload as { meta?: { name?: string } }).meta?.name ??
    null;

  // Extract selectedGroups from the pre-computed metrics payload
  const selectedGroups =
    (metricsPayload as { selectedGroups?: [string, string] | null }).selectedGroups ?? null;

  return {
    formatted,
    raw,
    symbol,
    companyName,
    availability,
    selectedGroups,
  };
};

export function useFinancialMetrics({
  category,
  autoFetch = true,
  date
}: UseFinancialMetricsOptions): UseFinancialMetricsReturn {
  const queryKey = useMemo(
    () => ['financial-metrics', category, date ?? 'current'],
    [category, date]
  );

  const query = useQuery({
    queryKey,
    enabled: autoFetch,
    staleTime: 60_000,
    gcTime: 5 * 60 * 1000,
    queryFn: async () => {
      const endpoint = date
        ? `/api/${category}/daily-financial-metrics?date=${date}`
        : `/api/${category}/daily-financial-metrics`;
      const response = await get(endpoint);
      return normaliseResponse(response);
    }
  });

  const errorMessage = useMemo(() => {
    if (!query.error) {
      return null;
    }

    if (query.error instanceof Error) {
      return query.error.message;
    }

    return 'Failed to load financial metrics';
  }, [query.error]);

  const refetch = useCallback(async () => {
    const result = await query.refetch({ throwOnError: true });
    if (result.error) {
      throw result.error;
    }
  }, [query]);

  return {
    metrics: query.data?.formatted ?? DEFAULT_FORMATTED,
    rawMetrics: query.data?.raw ?? null,
    hasData: query.data != null,
    isLoading: query.isLoading,
    error: errorMessage,
    refetch,
    category,
    symbol: query.data?.symbol ?? null,
    companyName: query.data?.companyName ?? null,
    dataAvailability: query.data?.availability ?? null,
    selectedGroups: query.data?.selectedGroups ?? null,
  };
}

export default useFinancialMetrics;
